import { useCallback, useEffect, useState } from "react";
import {
  getGoogleCalendarOAuthStart,
  getGoogleCalendarStatus,
  postGoogleCalendarDisconnect,
  postGoogleCalendarSync,
} from "@/lib/googleCalendarClient";

type Props = {
  organizationId: string | null;
};

/** Per-user Google Calendar OAuth link (WC-GCAL inbound). */
export function GoogleCalendarConnectSection({ organizationId }: Props) {
  const [linked, setLinked] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [oauthAvailable, setOauthAvailable] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    if (!organizationId) return;
    const r = await getGoogleCalendarStatus();
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setLinked(r.data.linked);
    setEmail(r.data.google_email);
    setOauthAvailable(r.data.oauth_available && r.data.oauth_configured);
    setLastSyncedAt(r.data.last_synced_at);
    setLastError(r.data.last_sync_error);
    setMsg("");
  }, [organizationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const connect = async () => {
    setBusy(true);
    setMsg("");
    const r = await getGoogleCalendarOAuthStart();
    setBusy(false);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    window.location.href = r.data.authorize_url;
  };

  const disconnect = async () => {
    setBusy(true);
    const r = await postGoogleCalendarDisconnect();
    setBusy(false);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setMsg("Google Calendar disconnected.");
    await load();
  };

  const syncNow = async () => {
    setBusy(true);
    const r = await postGoogleCalendarSync({ force: true });
    setBusy(false);
    if (!r.ok) {
      setMsg(r.error);
      return;
    }
    setMsg(`Synced — ${r.data.inserted} new, ${r.data.updated} updated (${r.data.events_today} today).`);
    await load();
  };

  if (!organizationId) return null;

  return (
    <div className="rounded-xl border border-emerald-200/80 dark:border-emerald-800/60 bg-emerald-50/30 dark:bg-emerald-950/20 px-4 py-4 space-y-2">
      <div>
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Google Calendar</p>
        <p className="text-[11px] text-gray-600 dark:text-gray-400 mt-1 leading-snug">
          Connect your Google account (separate from sign-in) to surface today&apos;s external calendar events in the
          activity bell. Turbo Assist–originated events are not duplicated.
        </p>
      </div>
      {linked ? (
        <p className="text-[11px] text-gray-700 dark:text-gray-300">
          Linked as <strong>{email || "Google account"}</strong>
          {lastSyncedAt ? ` · last sync ${new Date(lastSyncedAt).toLocaleString()}` : ""}
        </p>
      ) : (
        <p className="text-[11px] text-gray-500">Not linked for this workspace.</p>
      )}
      {lastError ? <p className="text-[11px] text-amber-700 dark:text-amber-300">Last error: {lastError}</p> : null}
      <div className="flex flex-wrap gap-2">
        {!linked && oauthAvailable ? (
          <button
            type="button"
            disabled={busy}
            className="rounded bg-emerald-600 text-white px-3 py-1 text-sm disabled:opacity-50"
            onClick={() => void connect()}
          >
            Connect Google Calendar
          </button>
        ) : null}
        {linked ? (
          <>
            <button type="button" disabled={busy} className="rounded border px-3 py-1 text-sm" onClick={() => void syncNow()}>
              Sync now
            </button>
            <button type="button" disabled={busy} className="rounded border px-3 py-1 text-sm" onClick={() => void disconnect()}>
              Disconnect
            </button>
          </>
        ) : null}
        {!oauthAvailable && !linked ? (
          <p className="text-[11px] text-gray-500">OAuth not configured on server (GOOGLE_CALENDAR_CLIENT_ID / SECRET).</p>
        ) : null}
      </div>
      {msg ? <p className="text-[11px] text-gray-700 dark:text-gray-300">{msg}</p> : null}
    </div>
  );
}
